import { ControlType, IEntity, Property, PropertyType } from "../../../base/types";
import { FC, useState } from "react";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { Registry } from "../../../base/register";
import { useGetProperties } from "../../../base/hooks";
import { ABCResponse, useCreateAbcIdMutation } from "./properties/abcIdApi";
import moment from "moment";
import { useAuth } from "../../../../modules/auth";
import { Spinner } from "../../../widgets";
import { useUpdateMutation } from "../../../services/data/tableDataApi";
import { IFieldValue } from "../../../services/models";
import { useNavigate } from "react-router-dom";
import { PageLinkHelper } from "../../../base/helpers";

export const ABCIDFormComponent: FC<IControlAttributes> = ({
    control,
    className,
    children,
    props,
    designerMode,
    ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const navigateTo: IPageLink | undefined = properties[Property.CancelPageLink];
    const navigateToLabel: string | undefined = properties[Property.Label];
    const [insertRecord] = useCreateAbcIdMutation()
    const [formData, setFormData] = useState({
        aadhaar_name: "",
        aadhaar_number: "",
        dob: "",
        gender: "",
        mobile: ""
    });
    const [errors, setErrors] = useState({
        aadhaar_name: "",
        aadhaar_number: "",
        dob: "",
        gender: "",
        mobile: ""
    });
    const { user, siteId } = useAuth();
    const navigate = useNavigate();
    const [updateRecord] = useUpdateMutation();
    const [abcResponse, setAbcResponse] = useState<ABCResponse>();
    const [showMessage, setSetShowMessage] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        let formattedValue = value;
        if (name === "dob" && value) {
            formattedValue = moment(value, "YYYY-MM-DD").format("DD/MM/YYYY");
        }

        setFormData({ ...formData, [name]: formattedValue });
        setErrors({ ...errors, [name]: "" });
    };

    const navigateToPage = () => {
        if (siteId && navigateTo && navigateTo.pageId) {
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateTo));
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);

        insertRecord({
            aadharName: formData.aadhaar_name,
            aadharNumber: formData.aadhaar_number,
            dob: formData.dob,
            gender: formData.gender,
            mobile: formData.mobile,
        })
            .unwrap()
            .then((data) => {
                setAbcResponse(data);
                if (data.status_code === 200) {
                    handleUpdateAbcID(data.abC_ACCOUNT_ID);
                } else {
                    setSetShowMessage(true);
                    setLoading(false);
                }
            })
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                console.error(message);
                return Promise.reject(message);
            })
    };

    const handleUpdateAbcID = (abcId: string) => {
        const record: IEntity = {
            edtec365_abcid: abcId,
        };
        saveFormData(record)
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                console.error(message);
            }).finally(() => {
                setLoading(false)
                setSetShowMessage(true);
            })
    };

    const saveFormData = (record: IEntity): Promise<IEntity> => {
        const fields: IFieldValue[] = Object.entries(record)
            .filter(([_, value]) => value !== undefined)
            .map(([field, value]) => ({ field, value, validate: true }));

        return updateRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "contact", sysId: user?.contactId, data: fields, triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                console.error(message);
                return Promise.reject(message);
            });
    };

    const formatInputDate = (dob: string) => {
        if (!dob) return "";
        return moment(dob, "DD/MM/YYYY").format("YYYY-MM-DD");
    };

    return (
        <div className="container mt-4 p-3 bg-white rounded">
            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label htmlFor="aadhaar_name" className="form-label">Aadhaar Name</label>
                        <input
                            type="text"
                            className="form-control"
                            id="aadhaar_name"
                            name="aadhaar_name"
                            value={formData.aadhaar_name}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="aadhaar_number" className="form-label">Aadhaar Number</label>
                        <input
                            type="text"
                            className="form-control"
                            id="aadhaar_number"
                            name="aadhaar_number"
                            value={formData.aadhaar_number}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="dob" className="form-label">Date of Birth</label>
                        <input
                            type="date"
                            className="form-control"
                            id="dob"
                            name="dob"
                            value={formatInputDate(formData.dob)}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label htmlFor="gender" className="form-label">Gender</label>
                        <select
                            className="form-control"
                            id="gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                        >
                            <option value="">Select Gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="O">Other</option>
                        </select>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="mobile" className="form-label">Mobile</label>
                        <input
                            type="text"
                            className="form-control"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-end mt-4 ">
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="btn btn-lg btn-primary me-3"
                >
                    <span className="indicator-label">
                        {loading && <Spinner className="me-2" show={loading} />}
                        <span>Submit</span>
                    </span>
                </button>
            </div>
            {(abcResponse && showMessage) &&
                <div className="mt-4 text-center">
                    <div className={abcResponse?.status_code === 200 ? "alert alert-success" : "alert alert-danger"}>
                        {abcResponse?.status_code === 200 ? (
                            <>
                                {abcResponse.message} {navigateToLabel}
                                <button type="button" onClick={navigateToPage} className="btn btn-link text-primary mx-2">Click Here</button>
                            </>
                        ) : (
                            abcResponse?.message
                        )}
                    </div>
                </div>
            }
        </div>
    );
};

Registry.addControl({
    id: "e8464c08-047d-4057-ab03-26f3bbee0d80",
    name: "ABC-ID Form Component",
    type: ControlType.ABCIDFormComponent,
    properties: [
        { id: Property.Tooltip, name: "Tooltip", type: PropertyType.String },
        { id: Property.Label, name: "Navigate Label", type: PropertyType.String },
        { id: Property.CancelPageLink, name: 'Navigate To', type: PropertyType.PageLink },
    ],
    component: ABCIDFormComponent,
});
